/**
 * MediaTranscriptPane — renders ONE uploaded transcript inside the Session
 * Transcript modal: an optional `<audio>`/`<video>` player plus the parsed
 * transcript as timestamped speaker rows, kept in sync with the media.
 *
 *   - The active cue (last one started at/before `currentTime`) highlights and
 *     auto-scrolls into view; clicking a cue seeks the media to its timestamp.
 *   - Sync is enabled only when the transcript parses to fully-timed cues
 *     (VTT/SRT/bracketed) AND media is loaded; an un-timed plain transcript
 *     still renders as text, just without seek/highlight.
 *   - Media bytes resolve from the local blob-store, self-healing from the
 *     BFF/Azure mirror on a cache miss (mirrors the AssetLocker pattern).
 *
 * Distinct from the legacy WS4 demo path in `TranscriptPlayback.tsx`, which
 * renders hardcoded fixture content and remains the no-upload fallback.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { loadBlob, saveBlob } from "@/lib/blob-store";
import {
  downloadWorkshopBlob,
  isWorkshopBlobSyncEnabled,
} from "@/lib/workshop-blob-sync";
import type { Transcript } from "@/stores/useTranscripts";
import {
  findActiveCueIndex,
  formatCueTime,
  parseTranscriptBody,
  type Cue,
} from "./transcript-cues";

type MediaStatus = "none" | "loading" | "ready" | "missing";

/** The legacy `.sp-*` palette (see `src/index.css`). Real transcripts have
 *  arbitrary speaker names, so assign a stable colour by hashing the name —
 *  deterministic per speaker, reuses the existing six accents. */
const SPEAKER_CLASSES = [
  "sp-jordan",
  "sp-aria",
  "sp-ben",
  "sp-priya",
  "sp-marcus",
  "sp-sam",
] as const;

function speakerClass(name: string | undefined): string {
  if (!name) return "sp-group";
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) | 0;
  return SPEAKER_CLASSES[Math.abs(h) % SPEAKER_CLASSES.length] ?? "sp-group";
}

function formatDate(ts: number): string {
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

/**
 * Resolve the transcript's media blob to an object URL, self-healing from the
 * BFF/Azure mirror on a local cache miss. Revokes the URL on unmount /
 * blob-key change; guards in-flight loads with a `cancelled` flag.
 */
function useTranscriptMediaUrl(transcript: Transcript): {
  url: string | null;
  status: MediaStatus;
} {
  const { mediaBlobKey, mediaPath, mediaMime } = transcript;
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<MediaStatus>(mediaBlobKey ? "loading" : "none");

  useEffect(() => {
    if (!mediaBlobKey) {
      setUrl(null);
      setStatus("none");
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setStatus("loading");

    void (async () => {
      let blob = await loadBlob(mediaBlobKey);
      // Self-heal: cache miss but a synced path exists → re-download + re-cache.
      if (!blob && mediaPath && isWorkshopBlobSyncEnabled()) {
        try {
          const fetched = await downloadWorkshopBlob(mediaPath);
          if (fetched) {
            blob = mediaMime ? new Blob([fetched], { type: mediaMime }) : fetched;
            void saveBlob(mediaBlobKey, blob);
          }
        } catch {
          /* fall through to "missing" */
        }
      }
      if (cancelled) return;
      if (!blob) {
        setStatus("missing");
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      setStatus("ready");
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaBlobKey, mediaPath, mediaMime]);

  return { url, status };
}

export function MediaTranscriptPane({
  transcript,
  onMediaEl,
}: {
  transcript: Transcript;
  /** Surfaces the live `<audio>`/`<video>` element to the host — the
   *  Playback modal's chapter/clip jumps seek it directly. */
  onMediaEl?: (el: HTMLMediaElement | null) => void;
}) {
  const parsed = useMemo(() => parseTranscriptBody(transcript.body), [transcript.body]);
  const sortedCues = useMemo(
    () =>
      parsed.synced ? [...parsed.cues].sort((a, b) => a.startMs - b.startMs) : parsed.cues,
    [parsed],
  );

  const { url, status } = useTranscriptMediaUrl(transcript);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const cueRefs = useRef<Array<HTMLDivElement | null>>([]);
  const activeIdxRef = useRef(-1);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [durationMs, setDurationMs] = useState<number | null>(null);

  const canSync = parsed.synced && status === "ready";

  const kind =
    transcript.mediaKind ??
    (transcript.mediaMime?.startsWith("video/") ? "video" : "audio");

  const speakers = useMemo(() => {
    const set = new Set<string>();
    for (const c of parsed.cues) if (c.speaker) set.add(c.speaker);
    return set;
  }, [parsed]);

  const onTimeUpdate = useCallback(() => {
    const el = mediaRef.current;
    if (!el || !parsed.synced) return;
    const idx = findActiveCueIndex(sortedCues, el.currentTime * 1000);
    if (idx !== activeIdxRef.current) {
      activeIdxRef.current = idx;
      setActiveIdx(idx);
    }
  }, [parsed.synced, sortedCues]);

  const onLoadedMetadata = useCallback(() => {
    const el = mediaRef.current;
    if (el && Number.isFinite(el.duration)) setDurationMs(el.duration * 1000);
  }, []);

  // Auto-scroll the active cue into view — only when the index changes, so a
  // 4Hz `timeupdate` doesn't thrash the scroll position.
  useEffect(() => {
    if (activeIdx < 0) return;
    cueRefs.current[activeIdx]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIdx]);

  function seekTo(cue: Cue): void {
    const el = mediaRef.current;
    if (!el || !Number.isFinite(cue.startMs)) return;
    el.currentTime = cue.startMs / 1000;
    const p = el.play?.();
    if (p) void p.catch(() => {});
  }

  const lastCueMs =
    parsed.synced && sortedCues.length > 0
      ? (sortedCues[sortedCues.length - 1]?.startMs ?? null)
      : null;
  const effectiveDurationMs = durationMs ?? lastCueMs;
  const durationLabel =
    effectiveDurationMs !== null ? formatCueTime(effectiveDurationMs) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Meta strip — derived from the real record (no fabricated accuracy). */}
      <div
        style={{
          display: "flex",
          flexShrink: 0,
          alignItems: "center",
          gap: 20,
          padding: "8px 16px",
          background: "var(--bg-panel-alt)",
          borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
          flexWrap: "wrap",
        }}
      >
        <MetaCell label="Added" value={formatDate(transcript.addedAt)} />
        <MetaCell label="By" value={transcript.addedBy || "—"} />
        {speakers.size > 0 && <MetaCell label="Speakers" value={String(speakers.size)} />}
        {durationLabel && <MetaCell label="Duration" value={durationLabel} />}
        <MetaCell
          label="Source"
          value={parsed.format.toUpperCase()}
          valueColor="var(--accent-sky)"
        />
      </div>

      {/* Media player (when attached + resolvable). */}
      {status === "ready" && url && (
        <div style={{ padding: "10px 16px", flexShrink: 0 }}>
          {kind === "video" ? (
            <video
              ref={(el) => {
                mediaRef.current = el;
                onMediaEl?.(el);
              }}
              src={url}
              controls
              preload="metadata"
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={onLoadedMetadata}
              style={{ width: "100%", maxHeight: "32vh", background: "#000", borderRadius: 6 }}
            />
          ) : (
            <audio
              ref={(el) => {
                mediaRef.current = el;
                onMediaEl?.(el);
              }}
              src={url}
              controls
              preload="metadata"
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={onLoadedMetadata}
              style={{ width: "100%" }}
            />
          )}
        </div>
      )}
      {status === "loading" && (
        <p style={{ padding: "10px 16px", fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
          Loading media…
        </p>
      )}
      {status === "missing" && (
        <p style={{ padding: "10px 16px", fontSize: "var(--fs-label)", color: "var(--accent-gold)" }}>
          Attached media is unavailable on this device — showing the transcript text only.
        </p>
      )}

      {/* Scrollable cue list (own scroll container so autoscroll stays local). */}
      <div className="trx-cue-pane">
        {sortedCues.length === 0 ? (
          <pre
            style={{
              margin: 0,
              padding: "14px 20px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "Inter, sans-serif",
              fontSize: "var(--fs-sub)",
              lineHeight: 1.65,
              color: "var(--text-secondary)",
            }}
          >
            {transcript.body}
          </pre>
        ) : (
          sortedCues.map((cue, i) => (
            <div
              key={cue.id}
              ref={(el) => {
                cueRefs.current[i] = el;
              }}
              className={cn(
                "trx-block",
                canSync && "is-clickable",
                canSync && i === activeIdx && "is-playing",
              )}
              {...(canSync
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: () => seekTo(cue),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        seekTo(cue);
                      }
                    },
                  }
                : {})}
            >
              <div className="trx-row">
                <span className="trx-time">{formatCueTime(cue.startMs)}</span>
                {cue.speaker && (
                  <span className={cn("trx-speaker", speakerClass(cue.speaker))}>
                    {cue.speaker}
                  </span>
                )}
                <div className="trx-text">{cue.text}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MetaCell({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <span
      style={{
        fontSize: "var(--fs-label)",
        color: "var(--text-muted)",
        letterSpacing: 1,
        textTransform: "uppercase",
      }}
    >
      {label}:{" "}
      <span style={{ color: valueColor ?? "var(--text-secondary)" }}>{value}</span>
    </span>
  );
}
